import { ChevronLeft, ChevronRight } from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn, getNameInitials } from '@/lib/utils';

export interface AttendanceReportItem {
  id: string;
  staffName: string;
  /** Empty when the record has no employee ID yet. */
  employeeId: string;
  /** The account type — Teacher, Accountant… */
  role: string;
  /** The designation typed on the record, or the account type when none was. */
  designation: string;
  workingDays: number;
  /** Days at work on time — late days are counted in `lateDays`, not here. */
  presentDays: number;
  lateDays: number;
  absentDays: number;
  leaveDays: number;
  /** "38h 20m" — summed over the punches with both ends. */
  hoursWorked: string;
  /** Null when no working day of the window has passed yet. */
  attendancePct: number | null;
  status: 'Excellent' | 'Good' | 'Average' | 'Needs Improvement' | null;
}

export interface AttendanceReportTableProps {
  rows: AttendanceReportItem[];
  isLoading: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

export function AttendanceReportTable({
  rows,
  isLoading,
  page,
  pageSize,
  total,
  onPageChange,
}: AttendanceReportTableProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startEntry = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endEntry = Math.min(page * pageSize, total);

  return (
    <div className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-200/90 dark:border-slate-800 shadow-xs overflow-hidden flex flex-col">
      <div className="w-full overflow-x-auto custom-scrollbar">
        <table className="w-full text-left border-collapse min-w-[820px]">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-[#0b1322] text-[13.5px] font-bold tracking-wide text-slate-700 dark:text-slate-300">
              <th scope="col" rowSpan={2} className="sm:sticky sm:left-0 z-30 py-3 px-3 w-12 min-w-[48px] max-w-[48px] text-center align-middle bg-slate-100 dark:bg-[#0b1322]">#</th>
              <th scope="col" rowSpan={2} className="sm:sticky sm:left-[48px] z-30 py-3 px-4 min-w-[220px] align-middle bg-slate-100 dark:bg-[#0b1322] sm:border-r border-slate-200 dark:border-slate-800 sm:shadow-[4px_0_8px_-3px_rgba(0,0,0,0.06)] dark:sm:shadow-[4px_0_8px_-3px_rgba(0,0,0,0.3)]">Staff Name</th>
              <th scope="col" rowSpan={2} className="py-3 px-4 text-center align-middle border-r border-slate-200 dark:border-slate-800 min-w-[110px]">Working Days</th>
              <th scope="col" colSpan={4} className="py-2 px-3 text-center border-b border-r border-slate-200 dark:border-slate-800">Summary</th>
              <th scope="col" rowSpan={2} className="py-3 px-4 text-center align-middle min-w-[110px]">Hours Worked</th>
              <th scope="col" rowSpan={2} className="py-3 px-4 text-center align-middle min-w-[110px]">Attendance %</th>
              <th scope="col" rowSpan={2} className="py-3 px-4 text-center align-middle min-w-[110px]">Status</th>
              {/* <th scope="col" rowSpan={2} className="sm:sticky sm:right-0 z-30 py-3 px-4 text-right align-middle min-w-[90px] bg-slate-100 dark:bg-[#0b1322] sm:border-l border-slate-200 dark:border-slate-800 sm:shadow-[-4px_0_8px_-3px_rgba(0,0,0,0.06)] dark:sm:shadow-[-4px_0_8px_-3px_rgba(0,0,0,0.3)]">Action</th> */}
            </tr>
            <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-100/90 dark:bg-[#0b1322]/90 text-[11.5px] font-bold text-slate-700 dark:text-slate-300">
              <th scope="col" className="py-1.5 px-3 text-center text-emerald-600 dark:text-emerald-400">Present</th>
              {/* Late is its own bucket beside Present: a late day is counted once,
                  here, and never in Present as well. */}
              <th scope="col" className="py-1.5 px-3 text-center text-amber-500 dark:text-amber-400">Late</th>
              <th scope="col" className="py-1.5 px-3 text-center text-rose-500 dark:text-rose-400">Absent</th>
              <th scope="col" className="py-1.5 px-3 text-center text-purple-600 dark:text-purple-400 border-r border-slate-200 dark:border-slate-800">On Leave</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 font-medium">
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="group border-b border-slate-200/80 dark:border-slate-800/80 bg-white dark:bg-[#111c2e] animate-pulse">
                  <td className="sm:sticky sm:left-0 z-20 py-3.5 px-3 w-12 min-w-[48px] max-w-[48px] bg-white dark:bg-[#111c2e]"><Skeleton className="h-4 w-5 mx-auto" /></td>
                  <td className="sm:sticky sm:left-[48px] z-20 py-3.5 px-4 min-w-[220px] bg-white dark:bg-[#111c2e] sm:border-r border-slate-200 dark:border-slate-800"><Skeleton className="h-4 w-32 mb-1.5" /><Skeleton className="h-3 w-44" /></td>
                  <td className="py-3.5 px-4"><Skeleton className="h-4 w-12 mx-auto" /></td>
                  <td className="py-3.5 px-3"><Skeleton className="h-4 w-8 mx-auto" /></td>
                  <td className="py-3.5 px-3"><Skeleton className="h-4 w-8 mx-auto" /></td>
                  <td className="py-3.5 px-3"><Skeleton className="h-4 w-8 mx-auto" /></td>
                  <td className="py-3.5 px-3"><Skeleton className="h-4 w-8 mx-auto" /></td>
                  <td className="py-3.5 px-4 text-center"><Skeleton className="h-4 w-14 mx-auto" /></td>
                  <td className="py-3.5 px-4 text-center"><Skeleton className="h-4 w-14 mx-auto" /></td>
                  <td className="py-3.5 px-4 text-center"><Skeleton className="h-6 w-20 mx-auto rounded-full" /></td>
                  {/* <td className="sm:sticky sm:right-0 z-20 py-3.5 px-4 text-right min-w-[90px] bg-white dark:bg-[#111c2e] sm:border-l border-slate-200 dark:border-slate-800"><Skeleton className="h-8 w-8 ml-auto rounded-lg" /></td> */}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="py-12 text-center">
                  <EmptyState
                    title="No report records found"
                    description="No staff attendance summary matches the selected criteria."
                  />
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => {
                return (
                  <tr
                    key={row.id}
                    className="group border-b border-slate-200/80 dark:border-slate-800/80 bg-white dark:bg-[#111c2e] hover:bg-slate-50/80 dark:hover:bg-[#16253d] transition-colors"
                  >
                    {/* Index */}
                    <td className="sm:sticky sm:left-0 z-20 py-3 px-3 w-12 min-w-[48px] max-w-[48px] text-center text-sm font-bold text-slate-400 bg-white dark:bg-[#111c2e] group-hover:bg-slate-50 dark:group-hover:bg-[#16253d]">
                      {(page - 1) * pageSize + idx + 1}
                    </td>

                    {/* Staff Name with Avatar + Employee ID + Role & Designation */}
                    <td className="sm:sticky sm:left-[48px] z-20 py-2.5 px-4 min-w-[220px] bg-white dark:bg-[#111c2e] group-hover:bg-slate-50 dark:group-hover:bg-[#16253d] sm:border-r border-slate-200 dark:border-slate-800 sm:shadow-[4px_0_8px_-3px_rgba(0,0,0,0.06)] dark:sm:shadow-[4px_0_8px_-3px_rgba(0,0,0,0.3)]">
                      <div className="flex items-start gap-2.5">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-[11px] font-black text-white shadow-2xs mt-0.5">
                          {getNameInitials(row.staffName)}
                        </span>
                        <div className="min-w-0">
                          <p className="font-extrabold text-sm text-slate-700 dark:text-white truncate">
                            {row.staffName}
                          </p>
                          <div className="flex flex-wrap flex-col items-start gap-x-1.5 text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">
                            {row.employeeId ? (
                              <span className="font-mono font-bold text-slate-700 dark:text-slate-300">{row.employeeId}</span>
                            ) : (
                              <span className="text-slate-400">No employee ID</span>
                            )}
                            <span className="truncate">{row.role}</span>
                            {/* Only when it says something the type does not. */}
                            {row.designation !== row.role && (
                              <span className="truncate">{row.designation}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Working Days */}
                    <td className="py-3 px-4 text-sm sm:text-base font-mono font-bold text-center text-slate-700 dark:text-slate-200 border-r border-slate-100 dark:border-slate-800/40">
                      {row.workingDays}
                    </td>

                    {/* Summary Columns */}
                    <td className="py-3 px-3 text-sm  sm:text-base font-mono font-bold text-center text-emerald-600 dark:text-emerald-400">
                      {row.presentDays}
                    </td>
                    <td className="py-3 px-3 text-sm  sm:text-base font-mono font-bold text-center text-amber-500 dark:text-amber-400">
                      {row.lateDays}
                    </td>
                    <td className="py-3 px-3 text-sm sm:text-base font-mono font-bold text-center text-rose-500 dark:text-rose-400">
                      {row.absentDays}
                    </td>
                    <td className="py-3 px-3 text-sm sm:text-base font-mono font-bold text-center text-purple-600 dark:text-purple-400 border-r border-slate-100 dark:border-slate-800/40">
                      {row.leaveDays}
                    </td>

                    {/* Hours Worked */}
                    <td className="py-3 px-4 text-sm sm:text-base font-mono font-bold text-center text-slate-700 dark:text-slate-200 whitespace-nowrap">
                      {row.hoursWorked}
                    </td>

                    {/* Attendance % */}
                    <td className="py-3 px-4 text-sm sm:text-base font-mono font-bold text-center">
                      {row.attendancePct === null ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <span
                          className={
                            row.attendancePct >= 90
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : row.attendancePct >= 75
                                ? 'text-amber-600 dark:text-amber-400'
                                : 'text-rose-600 dark:text-rose-400'
                          }
                        >
                          {row.attendancePct.toFixed(0)}%
                        </span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="py-3 px-4 text-center">
                      {row.status === null ? (
                        <span className="text-slate-400">—</span>
                      ) : row.status === 'Excellent' || row.status === 'Good' ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                          {row.status}
                        </span>
                      ) : row.status === 'Average' ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                          Average
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20 whitespace-nowrap">
                          Needs Improvement
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    {/* <td className="sm:sticky sm:right-0 z-20 py-3 px-4 text-right min-w-[90px] bg-white dark:bg-[#111c2e] group-hover:bg-slate-50 dark:group-hover:bg-[#16253d] sm:border-l border-slate-200 dark:border-slate-800 sm:shadow-[-4px_0_8px_-3px_rgba(0,0,0,0.06)] dark:sm:shadow-[-4px_0_8px_-3px_rgba(0,0,0,0.3)]">
                      <button
                        type="button"
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </td> */}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer & Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 border-t border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-[#0b1322]/50 text-xs text-slate-500 dark:text-slate-400">
        <div>
          Showing <span className="font-bold text-slate-700 dark:text-slate-200">{startEntry}</span> to{' '}
          <span className="font-bold text-slate-700 dark:text-slate-200">{endEntry}</span> of{' '}
          <span className="font-bold text-slate-700 dark:text-slate-200">{total}</span> entries
        </div>

        <div className="flex items-center gap-1 overflow-x-auto max-w-full pb-0.5">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all shrink-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
            const pageNumber = i + 1;
            const isActive = page === pageNumber;
            return (
              <button
                key={pageNumber}
                type="button"
                onClick={() => onPageChange(pageNumber)}
                className={cn(
                  'h-8 w-8 rounded-lg text-xs font-bold transition-all shrink-0',
                  isActive
                    ? 'bg-emerald-500 text-white shadow-xs'
                    : 'border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300',
                )}
              >
                {pageNumber}
              </button>
            );
          })}

          {totalPages > 5 && (
            <>
              <span className="px-1 text-slate-400 shrink-0">...</span>
              <button
                type="button"
                onClick={() => onPageChange(totalPages)}
                className={cn(
                  'h-8 w-8 rounded-lg text-xs font-bold transition-all shrink-0',
                  page === totalPages
                    ? 'bg-emerald-500 text-white shadow-xs'
                    : 'border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300',
                )}
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all shrink-0"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
